import { Injectable } from '@angular/core';

/**
 * Generic utility service for localStorage operations with type safety and default values.
 * Provides a reusable way to store and retrieve values from localStorage.
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageUtilityService {
  /**
   * Get a value from localStorage with a default fallback.
   * @param key The localStorage key
   * @param defaultValue The default value to return if key doesn't exist
   * @returns The stored value or the default value
   */
  get<T>(key: string, defaultValue: T): T {
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue === null) {
        return defaultValue;
      }
      
      // Try to parse as JSON, fallback to string comparison for booleans/strings
      try {
        return JSON.parse(storedValue) as T;
      } catch {
        // If JSON parsing fails, handle primitive types
        if (storedValue === 'true') {
          return (true as unknown) as T;
        }
        if (storedValue === 'false') {
          return (false as unknown) as T;
        }
        return (storedValue as unknown) as T;
      }
    } catch (error) {
      console.warn(`Failed to get value from localStorage for key "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Set a value in localStorage.
   * @param key The localStorage key
   * @param value The value to store (will be JSON stringified)
   */
  set<T>(key: string, value: T): void {
    try {
      const serializedValue = typeof value === 'string' 
        ? value 
        : JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.warn(`Failed to set value in localStorage for key "${key}":`, error);
    }
  }

  /**
   * Remove a value from localStorage.
   * @param key The localStorage key
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove value from localStorage for key "${key}":`, error);
    }
  }

  /**
   * Check if a key exists in localStorage.
   * @param key The localStorage key
   * @returns True if the key exists, false otherwise
   */
  has(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.warn(`Failed to check localStorage for key "${key}":`, error);
      return false;
    }
  }
}

